import flagsmith from 'flagsmith';
import { v4 as uuidv4 } from 'uuid';

import {
  FEATURE_FLAG_DEFAULTS,
  type FeatureFlag,
  type FeatureFlagTraits,
} from '@/constants/featureFlags';
import { segmentClient } from '@/util/analytics';
import { env } from '@/util/env';
import { Sentry } from '@/util/sentry';

export { flagsmith as featureFlagClient };

/**
 * Wait for feature flag initialization to complete.
 * Resolves immediately if already initialized or if no init in progress.
 */
export function waitForFeatureFlags(): Promise<void> {
  return initPromise ?? Promise.resolve();
}

/** How long the splash may wait on flags before falling back to defaults. */
const INIT_TIMEOUT_MS = 2000;

/**
 * Grace period for the background retry.
 *
 * Longer than the splash budget on purpose: nothing is waiting on it, so a
 * slow connection gets a real chance to finish rather than a second deadline.
 */
const INIT_RETRY_TIMEOUT_MS = 8000;

const INIT_RETRY_DELAY_MS = 500;

let initPromise: Promise<void> | null = null;

/**
 * The live `flagsmith.init()` call, shared by the splash attempt and the
 * background retry.
 *
 * The timeout below abandons the *race*, not the request — the SDK call keeps
 * running. Re-calling `flagsmith.init()` for the retry would therefore run two
 * inits concurrently against one client, which can double up listeners and let
 * the first land after the fallback has already resolved, flipping flags
 * mid-session. Cleared only on a real rejection, so a genuine failure can
 * still be retried with a fresh call.
 */
let inFlightInit: Promise<unknown> | null = null;

function startOrReuseInit(): Promise<unknown> {
  if (!inFlightInit) {
    inFlightInit = flagsmith
      .init({ environmentID: env.EXPO_PUBLIC_FLAGSMITH_SDK_KEY })
      .catch((err) => {
        inFlightInit = null;

        throw err;
      });
  }

  return inFlightInit;
}

/**
 * Turn an unknown rejection into a readable message.
 *
 * Flagsmith rejects with whatever its transport produced, which on web is
 * often a bare `XMLHttpRequestProgressEvent` or an `Error` with an empty
 * message. Captured as-is those reach Sentry as "Object captured as exception
 * with keys: ..." or "No error message", neither of which says what failed.
 */
function describeFlagsmithError(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }

  if (typeof err === 'string' && err) {
    return err;
  }

  if (typeof err === 'object' && err !== null) {
    const event = err as { type?: unknown; constructor?: { name?: string } };

    if (typeof event.type === 'string' && event.type) {
      return `${event.constructor?.name ?? 'Event'} (type=${event.type})`;
    }
  }

  return 'unknown error';
}

/** Wait out the shared init against a deadline, clearing the timer either way. */
async function awaitInitWithTimeout(timeoutMs: number): Promise<void> {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  try {
    await Promise.race([
      startOrReuseInit(),
      new Promise<never>((_, reject) => {
        timeoutHandle = setTimeout(() => reject(new Error('Flagsmith init timeout')), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutHandle != null) {
      clearTimeout(timeoutHandle);
    }
  }
}

/** Run one init attempt plus identification. Resolves true when flags are live. */
async function tryInitialize(timeoutMs: number, attemptLabel: string): Promise<boolean> {
  try {
    await awaitInitWithTimeout(timeoutMs);

    const anonId = await getOrCreateAnonId();

    await flagsmith.identify(anonId);

    return true;
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'featureFlags',
      level: 'info',
      message: `Flagsmith ${attemptLabel} failed: ${describeFlagsmithError(err)}`,
    });

    return false;
  }
}

/**
 * Keep trying after the splash has already been released on defaults.
 *
 * Nothing awaits this: flags that arrive late simply start resolving to real
 * values. Reports at `warning` only once the retry has also failed, since by
 * then the session really will run on `FEATURE_FLAG_DEFAULTS` throughout.
 */
async function retryInitializeInBackground(): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, INIT_RETRY_DELAY_MS));

  if (await tryInitialize(INIT_RETRY_TIMEOUT_MS, 'init retry')) {
    return;
  }

  console.warn('Flagsmith initialization failed, using defaults');

  Sentry.captureMessage('Flagsmith initialization failed after retry', {
    level: 'warning',
    tags: { context: 'featureFlags:init' },
  });
}

let cachedAnonId: string | null = null;

/** Milliseconds before giving up waiting for Segment's anonymous ID. */
const SEGMENT_ANON_ID_TIMEOUT_MS = 1000;

/**
 * Get the anonymous ID for Flagsmith identification.
 * Uses Segment's anonymous ID for consistency across analytics.
 * Falls back to a generated UUID if Segment ID is unavailable within SEGMENT_ANON_ID_TIMEOUT_MS.
 */
async function getOrCreateAnonId(): Promise<string> {
  if (cachedAnonId) {
    return cachedAnonId;
  }

  try {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const segmentAnonId = await Promise.race([
      Promise.resolve(segmentClient.getAnonymousId()).finally(
        () => timeoutHandle != null && clearTimeout(timeoutHandle)
      ),
      new Promise<null>((resolve) => {
        timeoutHandle = setTimeout(() => resolve(null), SEGMENT_ANON_ID_TIMEOUT_MS);
      }),
    ]);

    if (segmentAnonId) {
      cachedAnonId = segmentAnonId;

      return segmentAnonId;
    }
  } catch (err) {
    Sentry.captureException(err, {
      level: 'info',
      tags: { context: 'featureFlags:getOrCreateAnonId' },
    });
  }

  const fallbackId = `anon_${uuidv4()}`;

  cachedAnonId = fallbackId;

  return fallbackId;
}

/**
 * Initialize feature flag provider. Called once at app startup.
 * Re-entrant safe - returns existing promise if already initializing.
 */
export async function initializeFeatureFlagClient(): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  if (!env.EXPO_PUBLIC_FLAGSMITH_SDK_KEY) {
    console.warn('Flagsmith SDK key not configured, using defaults');

    Sentry.captureException(new Error('EXPO_PUBLIC_FLAGSMITH_SDK_KEY is not configured'), {
      level: 'warning',
      tags: { context: 'featureFlags:init' },
    });

    return;
  }

  // Only the first attempt is awaited. `waitForFeatureFlags` gates the splash,
  // so retrying inside this promise would hold a blank screen for the sum of
  // both deadlines — worst for exactly the slow connections that need the
  // retry. The retry runs unawaited instead.
  initPromise = tryInitialize(INIT_TIMEOUT_MS, 'init').then((succeeded) => {
    if (succeeded) {
      return;
    }

    retryInitializeInBackground().catch(() => {});
  });

  return initPromise;
}

/**
 * Set user identity for targeting. Called when user logs in.
 */
export async function identifyFeatureFlagUser(
  userId: string,
  traits?: FeatureFlagTraits
): Promise<void> {
  try {
    if (initPromise) {
      await initPromise;
    }

    await flagsmith.identify(userId, traits);
  } catch (err) {
    Sentry.captureMessage(`Flagsmith identify failed: ${describeFlagsmithError(err)}`, {
      level: 'warning',
      tags: { context: 'featureFlags:identify' },
    });
  }
}

/**
 * Clear user identity and re-identify as anonymous. Called when user logs out.
 */
export async function clearFeatureFlagIdentity(): Promise<void> {
  try {
    if (initPromise) {
      await initPromise;
    }

    await flagsmith.logout();

    const anonId = await getOrCreateAnonId();

    await flagsmith.identify(anonId);
  } catch (err) {
    Sentry.captureMessage(`Flagsmith logout failed: ${describeFlagsmithError(err)}`, {
      level: 'warning',
      tags: { context: 'featureFlags:logout' },
    });
  }
}

/**
 * Get a feature flag value. Waits for initialization if in progress.
 */
export async function getFeatureFlagValue(flag: FeatureFlag): Promise<boolean | string> {
  try {
    if (initPromise) {
      await initPromise;
    }

    return flagsmith.hasFeature(flag);
  } catch (err) {
    Sentry.captureMessage(`Flagsmith flag read failed: ${describeFlagsmithError(err)}`, {
      level: 'warning',
      tags: { context: 'getFeatureFlagValue', flag },
    });

    return FEATURE_FLAG_DEFAULTS[flag];
  }
}
